import {isEmpty} from 'lodash';
import * as React from 'react';
import {useCallback, useEffect, useState} from 'react';
import {IntlProvider} from 'react-intl';
import {connect, ConnectedProps} from 'react-redux';

import {CORE_MESSAGES_ID, I18N_REDUCER_STATE_ID} from '../i18n_constants';
import {i18nSlice} from '../i18n_slice';
import {
  changeCurrentLocale,
  loadI18nConfig,
  loadLocaleMessages,
  setI18nConfig,
} from '../i18n_thunks';
import {constants, LocaleMessagesByLocales} from 'src/js/common';
import {LogUtils} from 'src/js/common/utils';
import {I18nConfigRaw} from 'src/js/model/i18n_config';
import {RootState} from 'src/js/store';

const logger = LogUtils.getLogger(
  'IntlProviderContainer',
  constants.LIBRARY_NAME
);

interface OwnProps {
  staticI18nConfig?: I18nConfigRaw;
  staticLocaleMessages?: LocaleMessagesByLocales;
  children: React.ReactNode;
  dynamicLoaders: string[];
}

const mapState = (state: RootState) => ({
  currentLocale: state[I18N_REDUCER_STATE_ID].currentLocale,
  i18nConfig: state[I18N_REDUCER_STATE_ID].i18nConfig,
  localeMessages: state[I18N_REDUCER_STATE_ID].localeMessages,
  isInitialized: state[I18N_REDUCER_STATE_ID].isInitialized,
  isDynamicTranslationInitialized:
    state[I18N_REDUCER_STATE_ID].isDynamicTranslationInitialized,
});

const mapDispatch = {
  changeCurrentLocaleAC: changeCurrentLocale,
  initDynamicLoaders: i18nSlice.actions.initDynamicLoaders,
  loadI18nConfigAC: loadI18nConfig,
  loadLocaleMessagesAC: loadLocaleMessages,
  setI18nConfigAC: setI18nConfig,
  addLocaleMessages: i18nSlice.actions.addLocaleMessages,
  setIsInitialized: i18nSlice.actions.setIsInitialized,
};

const connector = connect(mapState, mapDispatch);
type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = PropsFromRedux & OwnProps;

export const IntlProviderContainerComponent: React.FC<Props> = (props) => {
  const {
    children,
    currentLocale,
    dynamicLoaders,
    i18nConfig,
    initDynamicLoaders,
    isDynamicTranslationInitialized,
    isInitialized,
    localeMessages,
    staticI18nConfig,
    setI18nConfigAC,
    setIsInitialized,
    addLocaleMessages,
    staticLocaleMessages,
    loadLocaleMessagesAC,
    loadI18nConfigAC,
  } = props;

  const [prevI18nConfig, setPrevI18nConfig] = useState<
    I18nConfigRaw | undefined
  >(undefined);

  // initialization of dynamic loaders
  useEffect(() => {
    if (isEmpty(isDynamicTranslationInitialized) && !isEmpty(dynamicLoaders)) {
      initDynamicLoaders({
        loaders: dynamicLoaders,
      });
    }
  }, [isDynamicTranslationInitialized, dynamicLoaders, initDynamicLoaders]);

  // initial loading of i18n config
  useEffect(() => {
    if (isEmpty(i18nConfig)) {
      if (isEmpty(staticI18nConfig)) {
        loadI18nConfigAC(true);
      } else {
        setI18nConfigAC(staticI18nConfig);
      }
    }
  }, [i18nConfig, staticI18nConfig, setI18nConfigAC, loadI18nConfigAC]);

  // initial loading of locale data
  useEffect(() => {
    const loadLocaleMessages = async (locale) => {
      if (isEmpty(localeMessages[locale])) {
        if (isEmpty(staticLocaleMessages)) {
          await loadLocaleMessagesAC(locale);
        } else {
          const currentLocaleMessages = staticLocaleMessages[locale];
          addLocaleMessages({
            messages: currentLocaleMessages,
            locale,
            id: CORE_MESSAGES_ID,
          });
        }
      }
    };

    if (isEmpty(prevI18nConfig) && !isEmpty(i18nConfig)) {
      // config was just loaded, init locales
      if (isEmpty(localeMessages[currentLocale])) {
        loadLocaleMessages(currentLocale).then(() => {
          setPrevI18nConfig(i18nConfig);
          setIsInitialized();
        });
      }
    }
  }, [
    currentLocale,
    prevI18nConfig,
    i18nConfig,
    loadLocaleMessagesAC,
    setIsInitialized,
    addLocaleMessages,
    localeMessages,
    staticLocaleMessages,
  ]);

  const handleError = useCallback((err) => {
    let shortMessage = err.message;
    if (err.message && err.message.indexOf('\n') >= 0) {
      shortMessage = err.message.substring(0, err.message.indexOf('\n'));
    }
    if (err.code === 'MISSING_TRANSLATION') {
      logger.warn(shortMessage);
      return;
    }
    if (err.code === 'FORMAT_ERROR') {
      logger.info(shortMessage);
      return;
    }
    throw err;
  }, []);

  return i18nConfig &&
    !isEmpty(localeMessages[currentLocale]) &&
    isInitialized ? (
    <IntlProvider
      locale={currentLocale}
      messages={localeMessages[currentLocale]}
      onError={handleError}
      textComponent={'span'}
    >
      {children}
    </IntlProvider>
  ) : (
    <span data-name={'intl-provider--not-loaded'}>IntlProvider not loaded</span>
  );
};

IntlProviderContainerComponent.displayName = 'IntlProviderContainer';

export const IntlProviderContainer = connector(
  React.memo(IntlProviderContainerComponent)
);
